import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import PhoneInput from '../../components/ui/PhoneInput';
import { apiService } from '../../services/api';
import VehicleInterestCard from '../../components/shared/VehicleInterestCard';

/* ─── Types ─── */
interface CreditApplication {
  id: string;
  tenant_id: string;
  reference_number?: string;
  status: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth?: string;
  ssn_last4?: string;
  monthly_income?: number;
  employment_status?: string;
  employer_name?: string;
  down_payment?: number;
  desired_monthly_payment?: number;
  vehicle_interest_id?: string;
  notes?: string;
  financing_route: string;
  assigned_lender?: string;
  lender_submission_status?: string;
  lender_notes?: string;
  customer_id?: string;
  deal_id?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  decision_notes?: string;
  created_at: string;
  updated_at: string;

  // New fields from expanded credit app
  middle_initial?: string;
  home_phone?: string;
  dl_number?: string;
  dl_state?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  housing_type?: string;
  monthly_rent_mortgage?: number;
  years_at_address?: number;
  months_at_address?: number;
  job_title?: string;
  employer_phone?: string;
  years_at_job?: number;
  months_at_job?: number;
  income_type?: string;
  vehicle_interest_text?: string;
  co_buyer_first_name?: string;
  co_buyer_last_name?: string;
  co_buyer_phone?: string;
  co_buyer_email?: string;
  co_buyer_relationship?: string;
  marketing_consent?: boolean;
  privacy_consent?: boolean;

  // Vehicle interests (from junction table API response)
  interested_vehicles?: Array<{
    id: string;
    year: number;
    make: string;
    model: string;
    trim?: string;
    stock_number?: string;
    asking_price?: number;
    image?: string;
    bhph_eligible: boolean;
    bhph_min_down_type?: string;
    bhph_min_down_value?: number;
    bhph_payment_amount?: number;
    bhph_payment_frequency?: string;
    bhph_term_months?: number;
  }>;
  vehicle_interest_count?: number;
}

/* ─── Helpers ─── */
const fmtCurrency = (value?: number) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const fmtDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  denied: 'bg-red-100 text-red-800',
};

const routeColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  bhph: 'bg-purple-100 text-purple-800',
  third_party_lender: 'bg-orange-100 text-orange-800',
  cash: 'bg-green-100 text-green-800',
  denied: 'bg-red-100 text-red-800',
};

const routeLabels: Record<string, string> = {
  pending: 'Pending',
  bhph: 'BHPH',
  third_party_lender: 'Third Party Lender',
  cash: 'Cash',
  denied: 'Denied',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  under_review: 'Under Review',
  approved: 'Approved',
  denied: 'Denied',
};

/* ─── Component ─── */
const CreditApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<CreditApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRoute, setFilterRoute] = useState('');

  const [selectedApp, setSelectedApp] = useState<CreditApplication | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable form state
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    date_of_birth: '',
    ssn_last4: '',
    monthly_income: '',
    employment_status: '',
    employer_name: '',
    down_payment: '',
    desired_monthly_payment: '',
    notes: '',
    status: '',
    financing_route: '',
    assigned_lender: '',
    lender_submission_status: '',
    decision_notes: '',
    middle_initial: '',
    home_phone: '',
    dl_number: '',
    dl_state: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    housing_type: '',
    monthly_rent_mortgage: '',
    years_at_address: '',
    months_at_address: '',
    job_title: '',
    employer_phone: '',
    years_at_job: '',
    months_at_job: '',
    income_type: '',
    vehicle_interest_text: '',
    co_buyer_first_name: '',
    co_buyer_last_name: '',
    co_buyer_phone: '',
    co_buyer_email: '',
    co_buyer_relationship: '',
  });

  const setField = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /* ─── Fetch ─── */
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterStatus) params.status = filterStatus;
      if (filterRoute) params.financing_route = filterRoute;
      const response = await apiService.get<CreditApplication[]>('/credit-applications/', {
        params,
      });
      setApplications(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching credit applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterRoute]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  /* ─── Select application ─── */
  const handleSelect = (app: CreditApplication) => {
    setSelectedApp(app);
    setEditing(false);
    populateForm(app);
    setMobileShowDetail(true);
  };

  const populateForm = (app: CreditApplication) => {
    setForm({
      first_name: app.first_name || '',
      last_name: app.last_name || '',
      phone: app.phone || '',
      email: app.email || '',
      date_of_birth: app.date_of_birth || '',
      ssn_last4: app.ssn_last4 || '',
      monthly_income: app.monthly_income?.toString() || '',
      employment_status: app.employment_status || '',
      employer_name: app.employer_name || '',
      down_payment: app.down_payment?.toString() || '',
      desired_monthly_payment: app.desired_monthly_payment?.toString() || '',
      notes: app.notes || '',
      status: app.status || 'pending',
      financing_route: app.financing_route || 'pending',
      assigned_lender: app.assigned_lender || '',
      lender_submission_status: app.lender_submission_status || '',
      decision_notes: app.decision_notes || '',
      middle_initial: app.middle_initial || '',
      home_phone: app.home_phone || '',
      dl_number: app.dl_number || '',
      dl_state: app.dl_state || '',
      street_address: app.street_address || '',
      city: app.city || '',
      state: app.state || '',
      zip_code: app.zip_code || '',
      housing_type: app.housing_type || '',
      monthly_rent_mortgage: app.monthly_rent_mortgage?.toString() || '',
      years_at_address: app.years_at_address?.toString() || '',
      months_at_address: app.months_at_address?.toString() || '',
      job_title: app.job_title || '',
      employer_phone: app.employer_phone || '',
      years_at_job: app.years_at_job?.toString() || '',
      months_at_job: app.months_at_job?.toString() || '',
      income_type: app.income_type || '',
      vehicle_interest_text: app.vehicle_interest_text || '',
      co_buyer_first_name: app.co_buyer_first_name || '',
      co_buyer_last_name: app.co_buyer_last_name || '',
      co_buyer_phone: app.co_buyer_phone || '',
      co_buyer_email: app.co_buyer_email || '',
      co_buyer_relationship: app.co_buyer_relationship || '',
    });
  };

  const handleClose = () => {
    setSelectedApp(null);
    setMobileShowDetail(false);
    setEditing(false);
  };

  /* ─── Save ─── */
  const handleSave = async () => {
    if (!selectedApp) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        email: form.email,
        date_of_birth: form.date_of_birth || null,
        ssn_last4: form.ssn_last4 || null,
        monthly_income: form.monthly_income ? parseFloat(form.monthly_income) : null,
        employment_status: form.employment_status || null,
        employer_name: form.employer_name || null,
        down_payment: form.down_payment ? parseFloat(form.down_payment) : null,
        desired_monthly_payment: form.desired_monthly_payment
          ? parseFloat(form.desired_monthly_payment)
          : null,
        notes: form.notes || null,
        status: form.status,
        financing_route: form.financing_route,
        assigned_lender:
          form.financing_route === 'third_party_lender' ? form.assigned_lender : null,
        lender_submission_status:
          form.financing_route === 'third_party_lender' && form.assigned_lender
            ? form.lender_submission_status
            : null,
        decision_notes: form.decision_notes || null,
        middle_initial: form.middle_initial || null,
        home_phone: form.home_phone || null,
        dl_number: form.dl_number || null,
        dl_state: form.dl_state || null,
        street_address: form.street_address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        housing_type: form.housing_type || null,
        monthly_rent_mortgage: form.monthly_rent_mortgage
          ? parseFloat(form.monthly_rent_mortgage)
          : null,
        years_at_address: form.years_at_address ? parseFloat(form.years_at_address) : null,
        months_at_address: form.months_at_address ? parseFloat(form.months_at_address) : null,
        job_title: form.job_title || null,
        employer_phone: form.employer_phone || null,
        years_at_job: form.years_at_job ? parseFloat(form.years_at_job) : null,
        months_at_job: form.months_at_job ? parseFloat(form.months_at_job) : null,
        income_type: form.income_type || null,
        vehicle_interest_text: form.vehicle_interest_text || null,
        co_buyer_first_name: form.co_buyer_first_name || null,
        co_buyer_last_name: form.co_buyer_last_name || null,
        co_buyer_phone: form.co_buyer_phone || null,
        co_buyer_email: form.co_buyer_email || null,
        co_buyer_relationship: form.co_buyer_relationship || null,
      };

      await apiService.patch(`/credit-applications/${selectedApp.id}/review`, payload);
      toast.success('Application saved');
      setEditing(false);
      await fetchApplications();
      // Update selected app locally
      setSelectedApp((prev) => (prev ? ({ ...prev, ...payload } as CreditApplication) : null));
    } catch (error) {
      console.error('Error saving application:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /* ─── Badges ─── */
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}
    >
      {statusLabels[status] || status}
    </span>
  );

  const RouteBadge: React.FC<{ route: string }> = ({ route }) => (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${routeColors[route] || 'bg-gray-100 text-gray-800'}`}
    >
      {routeLabels[route] || route}
    </span>
  );

  /* ─── Input helpers ─── */
  const inputCls =
    'w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500';
  const readOnlyCls =
    'w-full rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-sm text-gray-700';

  const EditableField: React.FC<{
    label: string;
    field: string;
    type?: string;
    placeholder?: string;
  }> = ({ label, field, type = 'text', placeholder }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {editing ? (
        type === 'tel' ? (
          <PhoneInput
            value={(form as Record<string, string>)[field] || ''}
            onChange={(v) => setField(field, v)}
            placeholder={placeholder}
            className={inputCls}
          />
        ) : (
          <input
            type={type}
            value={(form as Record<string, string>)[field] || ''}
            onChange={(e) => setField(field, e.target.value)}
            placeholder={placeholder}
            className={inputCls}
          />
        )
      ) : (
        <p className="text-sm text-gray-900 py-1.5">
          {type === 'number' && (form as Record<string, string>)[field]
            ? fmtCurrency(parseFloat((form as Record<string, string>)[field]))
            : (form as Record<string, string>)[field] || '—'}
        </p>
      )}
    </div>
  );

  /* ─── Render ─── */
  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Credit Applications</h1>
        <p className="mt-1 text-sm text-gray-600">
          Review and route financing applications. Click an application to review, edit, or route.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ─── LEFT: Application List ─── */}
        <div className={`lg:col-span-3 ${mobileShowDetail ? 'hidden lg:block' : ''}`}>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            {/* List header + filters */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <h2 className="text-lg font-semibold text-gray-900">Applications</h2>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    {applications.length}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                </select>

                <select
                  value={filterRoute}
                  onChange={(e) => setFilterRoute(e.target.value)}
                  className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All Routes</option>
                  <option value="pending">Pending</option>
                  <option value="bhph">BHPH</option>
                  <option value="third_party_lender">Third Party Lender</option>
                  <option value="cash">Cash</option>
                  <option value="denied">Denied</option>
                </select>
              </div>
            </div>

            {/* List body */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-500">Loading applications...</p>
                  </div>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-16">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
                  <p className="text-gray-500">
                    {filterStatus || filterRoute
                      ? 'Try adjusting your filters.'
                      : 'Credit applications will appear here as customers submit them.'}
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ref #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Applicant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Phone
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Income
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Down Pmt
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Route
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {applications.map((app) => (
                      <tr
                        key={app.id}
                        onClick={() => handleSelect(app)}
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                          selectedApp?.id === app.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-mono text-blue-600 whitespace-nowrap">
                          {app.reference_number || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {app.first_name} {app.last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {app.phone}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {fmtCurrency(app.monthly_income)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {fmtCurrency(app.down_payment)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={app.status} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <RouteBadge route={app.financing_route} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {fmtDate(app.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* ─── RIGHT: Detail / Edit Panel ─── */}
        <div
          className={`lg:col-span-2 ${!mobileShowDetail && !selectedApp ? 'hidden lg:block' : ''}`}
        >
          {selectedApp ? (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Panel header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedApp.first_name} {selectedApp.last_name}
                    </h3>
                    <button
                      onClick={handleClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors lg:hidden"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      {selectedApp.reference_number || 'No Ref'}
                    </span>
                    <span className="text-xs text-gray-500">
                      Submitted {fmtDate(selectedApp.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                    >
                      <PencilSquareIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  )}
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors hidden lg:block"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-5 max-h-[calc(100vh-14rem)] overflow-y-auto">
                {/* Quick Actions */}
                {selectedApp.phone && (
                  <a
                    href={`tel:${selectedApp.phone.replace(/[^0-9+]/g, '')}`}
                    className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                  >
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    Call {selectedApp.first_name} — {selectedApp.phone}
                  </a>
                )}

                {/* ─── Interested Vehicles ─── */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Interested Vehicles
                  </h4>
                  {selectedApp.interested_vehicles && selectedApp.interested_vehicles.length > 0 ? (
                    <div className="space-y-1.5">
                      {selectedApp.interested_vehicles.map((v) => (
                        <VehicleInterestCard key={v.id} vehicle={v} />
                      ))}
                    </div>
                  ) : selectedApp.vehicle_interest_text ? (
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-md p-2">
                      {selectedApp.vehicle_interest_text}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No vehicle selected</p>
                  )}
                </div>

                {/* ─── Personal Information ─── */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <EditableField label="First Name" field="first_name" />
                    <EditableField label="Last Name" field="last_name" />
                    <EditableField label="Phone" field="phone" type="tel" />
                    <EditableField label="Email" field="email" type="email" />
                    <EditableField label="Date of Birth" field="date_of_birth" type="date" />
                    <EditableField label="SSN Last 4" field="ssn_last4" placeholder="1234" />
                    <EditableField label="DL Number" field="dl_number" />
                    <EditableField label="DL State" field="dl_state" />
                  </div>
                </div>

                {/* ─── Residential Information ─── */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Residential Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <EditableField label="Street Address" field="street_address" />
                    <EditableField label="City" field="city" />
                    <EditableField label="State" field="state" />
                    <EditableField label="Zip Code" field="zip_code" />
                    <EditableField label="Housing Type" field="housing_type" />
                    <EditableField
                      label="Rent/Mortgage"
                      field="monthly_rent_mortgage"
                      type="number"
                      placeholder="0.00"
                    />
                    <EditableField
                      label="Years at Address"
                      field="years_at_address"
                      type="number"
                    />
                  </div>
                </div>

                {/* ─── Financial Information ─── */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Financial Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <EditableField
                      label="Monthly Income"
                      field="monthly_income"
                      type="number"
                      placeholder="0.00"
                    />
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Employment
                      </label>
                      {editing ? (
                        <select
                          value={form.employment_status}
                          onChange={(e) => setField('employment_status', e.target.value)}
                          className={inputCls}
                        >
                          <option value="">Select...</option>
                          <option value="employed">Employed</option>
                          <option value="self_employed">Self Employed</option>
                          <option value="retired">Retired</option>
                          <option value="unemployed">Unemployed</option>
                        </select>
                      ) : (
                        <p className="text-sm text-gray-900 py-1.5 capitalize">
                          {form.employment_status?.replace('_', ' ') || '—'}
                        </p>
                      )}
                    </div>
                    <EditableField label="Employer" field="employer_name" />
                    <EditableField
                      label="Down Payment"
                      field="down_payment"
                      type="number"
                      placeholder="0.00"
                    />
                    <EditableField
                      label="Desired Mo. Payment"
                      field="desired_monthly_payment"
                      type="number"
                      placeholder="0.00"
                    />
                    <EditableField label="Job Title" field="job_title" />
                    <EditableField label="Employer Phone" field="employer_phone" type="tel" />
                    <EditableField label="Years at Job" field="years_at_job" type="number" />
                    <EditableField label="Income Type" field="income_type" />
                  </div>
                </div>

                {/* ─── Notes ─── */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Applicant Notes
                  </h4>
                  {editing ? (
                    <textarea
                      value={form.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                      rows={3}
                      className={inputCls}
                      placeholder="Notes from the applicant or phone call..."
                    />
                  ) : (
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-md p-2 min-h-[2rem]">
                      {form.notes || 'No notes'}
                    </p>
                  )}
                </div>

                {/* ─── Co-Buyer Information ─── */}
                {(editing || selectedApp.co_buyer_first_name) && (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Co-Buyer Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <EditableField label="First Name" field="co_buyer_first_name" />
                      <EditableField label="Last Name" field="co_buyer_last_name" />
                      <EditableField label="Phone" field="co_buyer_phone" type="tel" />
                      <EditableField label="Email" field="co_buyer_email" type="email" />
                      <EditableField label="Relationship" field="co_buyer_relationship" />
                    </div>
                  </div>
                )}

                {/* ─── Review Decision ─── */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1.5" />
                    Review Decision
                  </h4>

                  <div className="space-y-3">
                    {/* Status */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                      <select
                        value={form.status}
                        onChange={(e) => setField('status', e.target.value)}
                        disabled={!editing}
                        className={editing ? inputCls : readOnlyCls}
                      >
                        <option value="pending">Pending</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                        <option value="denied">Denied</option>
                      </select>
                    </div>

                    {/* Financing Route */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        Financing Route
                      </label>
                      <div className="space-y-1.5">
                        {[
                          { value: 'bhph', label: 'BHPH (In-House)' },
                          { value: 'third_party_lender', label: 'Send to Lender' },
                          { value: 'cash', label: 'Cash' },
                          { value: 'denied', label: 'Deny' },
                        ].map((option) => (
                          <label
                            key={option.value}
                            className="flex items-center space-x-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="financing_route"
                              value={option.value}
                              checked={form.financing_route === option.value}
                              onChange={(e) => {
                                setField('financing_route', e.target.value);
                                if (e.target.value !== 'third_party_lender') {
                                  setField('assigned_lender', '');
                                  setField('lender_submission_status', '');
                                }
                              }}
                              disabled={!editing}
                              className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{option.label}</span>
                          </label>
                        ))}
                      </div>
                      {form.financing_route === 'bhph' &&
                        selectedApp.interested_vehicles?.some((v) => !v.bhph_eligible) && (
                          <p className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mt-1">
                            One or more selected vehicles are not marked as BHPH eligible.
                          </p>
                        )}
                    </div>

                    {/* Lender fields */}
                    {form.financing_route === 'third_party_lender' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Lender
                          </label>
                          <select
                            value={form.assigned_lender}
                            onChange={(e) => setField('assigned_lender', e.target.value)}
                            disabled={!editing}
                            className={editing ? inputCls : readOnlyCls}
                          >
                            <option value="">Select Lender...</option>
                            <option value="Westlake">Westlake</option>
                            <option value="CAC">CAC</option>
                            <option value="Western Funding">Western Funding</option>
                          </select>
                        </div>
                        {form.assigned_lender && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Lender Status
                            </label>
                            <select
                              value={form.lender_submission_status}
                              onChange={(e) => setField('lender_submission_status', e.target.value)}
                              disabled={!editing}
                              className={editing ? inputCls : readOnlyCls}
                            >
                              <option value="">Select Status...</option>
                              <option value="not_submitted">Not Submitted</option>
                              <option value="submitted">Submitted</option>
                              <option value="approved">Approved</option>
                              <option value="denied">Denied</option>
                              <option value="stipulations">Stipulations</option>
                            </select>
                          </div>
                        )}
                      </>
                    )}

                    {/* Decision Notes */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Decision Notes
                      </label>
                      <textarea
                        value={form.decision_notes}
                        onChange={(e) => setField('decision_notes', e.target.value)}
                        rows={3}
                        disabled={!editing}
                        className={editing ? inputCls : readOnlyCls}
                        placeholder="Internal notes about this decision..."
                      />
                    </div>
                  </div>
                </div>

                {/* ─── Action Buttons ─── */}
                {editing && (
                  <div className="border-t border-gray-200 pt-4 flex space-x-3">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-4 py-2 text-sm font-semibold transition-colors flex items-center justify-center"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(false);
                        if (selectedApp) populateForm(selectedApp);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm hidden lg:flex items-center justify-center py-24">
              <div className="text-center">
                <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Select an application to review</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditApplicationsPage;
